import './progress.css';
import {
  fetchWeights,
  fetchWeekCalories,
  movingAverage,
  computeWeeklyStats,
  estimateWeeklyPace,
  fetchPeriodReport,
  type WeightPoint,
  type DayCalories,
  type WeeklyStats,
  type PeriodReport,
} from '../services/stats';
import {
  fetchTargets, fetchAdaptiveProfileInfo, acceptAdaptiveTarget,
  type DailyTargets, type AdaptiveProfileInfo,
} from '../services/entries';
import { computeAdaptiveTarget, calcAge } from '../lib/goals';
import { toast } from '../lib/ui';
import { reportError } from '../lib/sentry';
import { isTransient } from '../lib/netRetry';

const fmt = (n: number): string => Math.round(n).toLocaleString('he-IL');

/** מספר בסימן ובספרה אחת, עם LRM כדי שהמינוס לא יתהפך ב-RTL */
function signed1(n: number): string {
  const r = Math.round(n * 10) / 10;
  const sign = r > 0 ? '+' : r < 0 ? '-' : '';
  return `‎${sign}${Math.abs(r).toFixed(1)}`;
}

const DAY_LETTER = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];
const DAY_NAME = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

/** YYYY-MM-DD מקומי → Date מקומי */
function parseLocal(date: string): Date {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, m - 1, d);
}

// ---------- כרטיס משקל ----------

/** נקודה לתצוגה בגרף — יומית (fromDate/toDate ריקים) או מקובצת (ממוצע על טווח) */
interface DotPoint extends WeightPoint { fromDate?: string; toDate?: string }

/** קיבוץ נקודות התצוגה לפי טווח הצפייה: שבוע/חודש = יומי; 6 חודשים = שבועי (א'-ש',
 *  עקבי עם יישור הדוח ללוח-שנה); שנה = חודשי. משמש רק לנקודות/מגע — קו המגמה נשאר חלק ויומי. */
function groupWeightPoints(points: WeightPoint[], rangeDays: number): DotPoint[] {
  if (rangeDays <= 30) return points.map((p) => ({ ...p }));
  const monthly = rangeDays >= 365;
  const keyOf = (d: Date) =>
    monthly ? `${d.getFullYear()}-${d.getMonth()}` : localDateStr(dayBackFrom(d, d.getDay()));
  const buckets = new Map<string, WeightPoint[]>();
  for (const p of points) {
    const k = keyOf(parseLocal(p.date));
    const arr = buckets.get(k);
    if (arr) arr.push(p); else buckets.set(k, [p]);
  }
  const out: DotPoint[] = [];
  for (const group of buckets.values()) {
    const sorted = [...group].sort((a, b) => a.date.localeCompare(b.date));
    const avg = sorted.reduce((s, p) => s + p.weightKg, 0) / sorted.length;
    out.push({ date: sorted[sorted.length - 1].date, weightKg: avg, fromDate: sorted[0].date, toDate: sorted[sorted.length - 1].date });
  }
  return out.sort((a, b) => a.date.localeCompare(b.date));
}

function dayBackFrom(anchor: Date, back: number): Date {
  return new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate() - back);
}

function weightChartSvg(points: WeightPoint[], trend: WeightPoint[], dots: DotPoint[]): string {
  // תחום ערכים עם שוליים; קווי רשת על ק"ג עגולים
  const values = [...points, ...trend].map((p) => p.weightKg);
  const lo = Math.floor(Math.min(...values) - 0.3);
  const hi = Math.ceil(Math.max(...values) + 0.3);
  const step = Math.max(1, Math.ceil((hi - lo) / 4));
  const y = (v: number) => 18 + ((hi - v) / (hi - lo)) * 120;

  // ציר זמן לפי מרחק ימים אמיתי (פערים בין שקילות נשמרים) — תמיד לפי הנתונים היומיים המלאים
  const t0 = parseLocal(points[0].date).getTime();
  const span = Math.max(1, parseLocal(points[points.length - 1].date).getTime() - t0);
  const x = (date: string) => 44 + ((parseLocal(date).getTime() - t0) / span) * 252;

  let grid = '';
  for (let t = lo; t <= hi; t += step) {
    const gy = y(t).toFixed(1);
    grid += `<line x1="34" y1="${gy}" x2="312" y2="${gy}" stroke="var(--hairline)" stroke-width="1"/>
      <text x="26" y="${(y(t) + 4).toFixed(1)}" font-size="10" fill="var(--ink-3)" text-anchor="end">${t}</text>`;
  }

  // כל נקודה: עיגול נראה + עיגול-מגע גדול ושקוף (קל יותר לגעת באצבע), עם תאריך/משקל(/טווח) כ-data
  const dotsHtml = dots
    .map((p) => {
      const cx = x(p.date).toFixed(1);
      const cy = y(p.weightKg).toFixed(1);
      const range = p.fromDate && p.toDate && p.fromDate !== p.toDate
        ? ` data-from="${p.fromDate}" data-to="${p.toDate}"` : '';
      return `<g class="wt-dot" data-date="${p.date}" data-w="${p.weightKg}"${range}>
        <circle cx="${cx}" cy="${cy}" r="10" fill="transparent" class="wt-dot-hit"/>
        <circle cx="${cx}" cy="${cy}" r="4" fill="#CFE5DA" class="wt-dot-vis"/>
      </g>`;
    })
    .join('');
  const path = trend
    .map((p, i) => `${i ? 'L' : 'M'}${x(p.date).toFixed(1)} ${y(p.weightKg).toFixed(1)}`)
    .join(' ');

  // נקודת סיום מודגשת + תווית ערך (נזהרת לא להיחתך בקצוות)
  const end = trend[trend.length - 1];
  const ex = x(end.date);
  const ey = y(end.weightKg);
  const labelY = ey < 32 ? ey + 22 : ey - 12;
  const labelX = Math.min(300, Math.max(20, ex));

  return `
    <svg class="prog-chart wt-chart" viewBox="0 0 320 150" width="100%" role="img" aria-label="גרף משקל — הקישו על נקודה לפרטים">
      ${grid}
      <g>${dotsHtml}</g>
      <path d="${path}" fill="none" stroke="var(--fat)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
      <circle cx="${ex.toFixed(1)}" cy="${ey.toFixed(1)}" r="5.5" fill="var(--fat)" stroke="#FFFFFF" stroke-width="2"/>
      <text x="${labelX.toFixed(1)}" y="${labelY.toFixed(1)}" font-size="11" font-weight="700" fill="var(--ink)" text-anchor="middle">${end.weightKg.toFixed(1)}</text>
    </svg>`;
}

/** מחבר מגע/קליק על נקודות הגרף לשורת הפרטים — מעדכן טקסט + מדגיש את הנקודה שנבחרה */
function wireWeightChartTaps(card: HTMLElement): void {
  const focusEl = card.querySelector<HTMLElement>('#wt-focus');
  if (!focusEl) return;
  const dateFmt = new Intl.DateTimeFormat('he-IL', { day: 'numeric', month: 'long' });
  const dateFmt2 = new Intl.DateTimeFormat('he-IL', { day: 'numeric', month: 'numeric' });
  card.querySelectorAll<SVGGElement>('.wt-dot').forEach((g) => {
    g.addEventListener('click', () => {
      card.querySelectorAll('.wt-dot-vis.on').forEach((el) => el.classList.remove('on'));
      g.querySelector('.wt-dot-vis')?.classList.add('on');
      const w = Number(g.dataset.w);
      const from = g.dataset.from, to = g.dataset.to;
      if (from && to) {
        const range = `${dateFmt2.format(parseLocal(from))}–${dateFmt2.format(parseLocal(to))}`;
        focusEl.textContent = `${range} · ממוצע ${w.toFixed(1)} ק"ג`;
      } else {
        const date = g.dataset.date!;
        focusEl.textContent = `${dateFmt.format(parseLocal(date))} · ${w.toFixed(1)} ק"ג`;
      }
      focusEl.classList.add('picked');
    });
  });
}

const WEIGHT_RANGES: { days: number; label: string }[] = [
  { days: 7, label: 'שבוע' },
  { days: 30, label: 'חודש' },
  { days: 180, label: '6 חודשים' },
  { days: 365, label: 'שנה' },
];

/** תאריך הגעה משוער ליעד — לפי קצב "רחב" (רגרסיה על כל קו-המגמה), לא תנודה שבועית בודדת. */
function etaLine(latestWeightKg: number, info: AdaptiveProfileInfo | null, paceKgPerWeek: number | null): string {
  if (!info || info.goalWeightKg == null || paceKgPerWeek == null) return '';
  const remaining = latestWeightKg - info.goalWeightKg; // חיובי = עוד יש לרדת
  if (remaining <= 0) return `<div class="prog-eta good">🎉 הגעת ליעד המשקל!</div>`;
  if (paceKgPerWeek <= 0.03) return ''; // אין קצב אמיתי לחזות ממנו — לא מנחשים סתם
  const weeksLeft = remaining / paceKgPerWeek;
  const farAway = weeksLeft > 78; // מעל שנה וחצי — תאריך מדויק פחות משמעותי
  const dateTxt = farAway
    ? 'מעל שנה מהיום — בקצב הנוכחי אולי שווה לשקול להאיץ אותו'
    : `סביב ${new Intl.DateTimeFormat('he-IL', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(Date.now() + weeksLeft * 7 * 86400000))}`;
  const verb = info.sex === 'male' ? 'צפוי' : 'צפויה';
  return `<div class="muted prog-eta">בקצב הנוכחי (~${paceKgPerWeek.toFixed(1)} ק"ג/שבוע) ${verb} להגיע ליעד (${fmt(info.goalWeightKg)} ק"ג) ${dateTxt}</div>`;
}

function weightCardHtml(points: WeightPoint[], trend: WeightPoint[], info: AdaptiveProfileInfo | null, pace: number | null, rangeDays: number): string {
  if (!points.length) {
    return `
      <div class="card prog-empty">
        <div class="big">⚖️</div>
        <p>עוד אין מספיק שקילות — אחרי כמה ימים יופיע כאן גרף 🌱</p>
      </div>`;
  }
  const last = points[points.length - 1];
  const diff = last.weightKg - points[0].weightKg;
  const r = Math.round(diff * 10) / 10;
  let delta = '';
  if (points.length >= 2) {
    if (r < 0) delta = `<span class="prog-delta">${signed1(diff)} ק"ג מההתחלה ↓</span>`;
    else if (r > 0) delta = `<span class="prog-delta flat">${signed1(diff)} ק"ג מההתחלה ↑</span>`;
    else delta = `<span class="prog-delta flat">ללא שינוי מההתחלה</span>`;
  }
  const rangeToggle = `
    <div class="rep-toggle wt-toggle">
      ${WEIGHT_RANGES.map((r2) => `<button class="rep-per${rangeDays === r2.days ? ' on' : ''}" data-wtrange="${r2.days}">${r2.label}</button>`).join('')}
    </div>`;
  const dotsDesc = rangeDays >= 365 ? 'הנקודות הן ממוצע חודשי' : rangeDays > 30 ? 'הנקודות הן ממוצע שבועי' : 'הנקודות הן שקילות יומיות';
  const chart =
    points.length >= 3
      ? `<div class="h-row wt-range-row">
          <div class="muted prog-sub">הקו הוא ממוצע המגמה, ${dotsDesc}</div>
          ${rangeToggle}
        </div>
        ${weightChartSvg(points, trend, groupWeightPoints(points, rangeDays))}
        <div class="muted prog-sub wt-focus" id="wt-focus">הקישו על נקודה לתאריך ומשקל מדויקים</div>`
      : `<p class="prog-encourage muted">עוד אין מספיק שקילות — אחרי כמה ימים יופיע כאן גרף 🌱</p>`;
  return `
    <div class="card">
      <div class="h-row">
        <div class="prog-hero"><span class="num">${last.weightKg.toFixed(1)}</span><span class="unit">ק"ג</span></div>
        ${delta}
      </div>
      ${chart}
      ${etaLine(trend[trend.length - 1].weightKg, info, pace)}
    </div>`;
}

// ---------- אריחי סיכום ----------

function tilesHtml(stats: WeeklyStats): string {
  const change = stats.weightChangeKg;
  return `
    <div class="prog-tiles">
      <div class="tile"><div class="v">${stats.avgCalories != null ? fmt(stats.avgCalories) : '—'}</div><div class="k">ממוצע קק"ל יומי השבוע</div></div>
      <div class="tile"><div class="v">${stats.loggedDays}/7</div><div class="k">ימי תיעוד השבוע</div></div>
      <div class="tile"><div class="v">${change != null ? signed1(change) : '—'}</div><div class="k">ק"ג בשבוע האחרון</div></div>
    </div>`;
}

// ---------- כרטיס קלוריות שבועי ----------

function weekBarsSvg(week: DayCalories[], target: number | null): string {
  const maxV = Math.max(target ?? 0, ...week.map((d) => d.calories), 1);
  const yOf = (v: number) => 100 - (v / maxV) * 76;
  let bars = '';
  let labels = '';
  let letters = '';
  week.forEach((d, i) => {
    const cx = 41 + i * 40;
    letters += `<text x="${cx}" y="114">${DAY_LETTER[parseLocal(d.date).getDay()]}</text>`;
    if (!d.logged) return; // יום לא מתועד — בלי עמודה
    const h = Math.max(3, (d.calories / maxV) * 76);
    const top = 100 - h;
    const r = Math.min(4, h / 2);
    const xL = cx - 11;
    const over = target != null && d.calories > target;
    // עמודה ברוחב 22 עם פינות מעוגלות למעלה בלבד
    bars += `<path d="M${xL} 100 V${(top + r).toFixed(1)} Q${xL} ${top.toFixed(1)} ${(xL + r).toFixed(1)} ${top.toFixed(1)} H${(xL + 22 - r).toFixed(1)} Q${xL + 22} ${top.toFixed(1)} ${xL + 22} ${(top + r).toFixed(1)} V100 Z" fill="${over ? 'var(--ring)' : '#7FB894'}"/>`;
    if (over)
      labels += `<text x="${cx}" y="${Math.max(10, top - 8).toFixed(1)}" font-size="10.5" font-weight="700" fill="var(--ink)" text-anchor="middle">${fmt(d.calories)}</text>`;
  });
  const targetLine =
    target != null
      ? `<line x1="16" y1="${yOf(target).toFixed(1)}" x2="308" y2="${yOf(target).toFixed(1)}" stroke="#9A9088" stroke-width="1" opacity=".55"/>`
      : '';
  return `
    <svg class="prog-chart" viewBox="0 0 320 120" width="100%" role="img" aria-label="קלוריות שבועיות">
      <line x1="16" y1="100" x2="308" y2="100" stroke="#C9BFB2" stroke-width="1"/>
      ${targetLine}
      ${bars}
      ${labels}
      <g font-size="10" fill="var(--ink-3)" text-anchor="middle">${letters}</g>
    </svg>`;
}

/** כיתוב תחתון בטון לא שיפוטי */
function weekFooter(week: DayCalories[], target: number | null): string {
  const logged = week.filter((d) => d.logged);
  if (!logged.length) return 'עוד אין תיעוד השבוע — מתחילים מהיום 🌱';
  if (target == null) return 'אין עדיין יעד קלורי בפרופיל — אחרי שנשלים אותו יופיע כאן קו היעד.';
  const over = logged.filter((d) => d.calories > target);
  const avg = logged.reduce((s, d) => s + d.calories, 0) / logged.length;
  const avgTxt = avg <= target ? 'השבוע בממוצע: מתחת ליעד.' : 'השבוע בממוצע: קצת מעל היעד.';
  if (!over.length) return `כל הימים שתועדו בתוך היעד — עבודה יפה 💚 ${avgTxt}`;
  const overTxt =
    over.length === 1
      ? `${DAY_NAME[parseLocal(over[0].date).getDay()]} חרג מהיעד — קרה, ממשיכים 💚`
      : `${over.length} ימים חרגו מהיעד — קרה, ממשיכים 💚`;
  return `${overTxt} ${avgTxt}`;
}

function weekCardHtml(week: DayCalories[], target: number | null): string {
  return `
    <div class="card">
      <div class="h-row prog-week-head">
        <b>קלוריות השבוע</b>
        ${target != null ? `<span class="muted">— היעד: ${fmt(target)}</span>` : ''}
      </div>
      ${weekBarsSvg(week, target)}
      <div class="muted prog-week-note">${weekFooter(week, target)}</div>
    </div>`;
}

// ---------- דוח תקופתי (עודף/גירעון) ----------

const signedKg = (n: number): string => `‎${n > 0 ? '-' : n < 0 ? '+' : ''}${Math.abs(n).toFixed(1)}`;
const KCAL_PER_KG = 7700;

/** אחזקה אמיתית ("נייטרלי") משתמעת מהתקופה: שינוי המשקל בפועל, לא היעד המתוכנן.
 *  אותו מקור-אמת גם לשורות בדוח וגם לקלט של הצעת היעד המותאם (renderAdaptive).
 *  deficitPerDay: חיובי = גירעון אמיתי מול הנייטרלי (אוכלת פחות מהאחזקה בפועל). */
export function impliedMaintenanceOf(report: PeriodReport): { maintenance: number; deficitPerDay: number } | null {
  if (report.actualWeightChangeKg == null || report.avgCalories == null) return null;
  const deficitPerDay = (report.actualWeightChangeKg * KCAL_PER_KG) / report.days;
  return { maintenance: report.avgCalories + deficitPerDay, deficitPerDay };
}

function reportEnoughLogging(report: PeriodReport): boolean {
  return report.loggedDays >= Math.max(3, Math.ceil(report.days * 0.4));
}

function reportCardHtml(report: PeriodReport, periodDays: number): string {
  const rows: string[] = [];
  const balance = report.deficitPerDay;
  let verdict = '';
  let verdictClass = 'neutral';
  let neutralExplain = '';

  if (report.avgCalories == null || report.target == null) {
    verdict = report.target == null
      ? 'נשלים יעד קלורי בפרופיל כדי לראות אם את בגירעון או בעודף.'
      : 'עוד אין מספיק תיעוד בתקופה הזו — נמשיך לתעד וזה יופיע כאן.';
  } else {
    rows.push(`<div class="rep-row"><span>ממוצע יומי</span><b>${fmt(report.avgCalories)} קק"ל</b></div>`);
    rows.push(`<div class="rep-row"><span>היעד</span><b>${fmt(report.target)} קק"ל</b></div>`);
    if (balance != null) {
      const isDeficit = balance > 0;
      rows.push(`<div class="rep-row"><span>${isDeficit ? 'גירעון' : 'עודף'} מול היעד</span><b class="${isDeficit ? 'good' : 'warn'}">${fmt(Math.abs(balance))} קק"ל</b></div>`);
    }
    if (report.actualWeightChangeKg != null) {
      rows.push(`<div class="rep-row"><span>שינוי משקל בפועל</span><b>${signedKg(report.actualWeightChangeKg)} ק"ג</b></div>`);
    }
    const enoughLogging = reportEnoughLogging(report);
    const neutralInfo = enoughLogging ? impliedMaintenanceOf(report) : null;
    if (neutralInfo != null) {
      const isRealDeficit = neutralInfo.deficitPerDay > 0;
      rows.push(`
        <div class="rep-row">
          <span>אחזקה אמיתית (נייטרלי) <button type="button" class="rep-q" data-neutral-info aria-label="מה זה נייטרלי?">?</button></span>
          <b>${fmt(neutralInfo.maintenance)} קק"ל</b>
        </div>`);
      rows.push(`
        <div class="rep-row">
          <span>${isRealDeficit ? 'גירעון' : 'עודף'} מול הנייטרלי</span>
          <b class="${isRealDeficit ? 'good' : 'warn'}">${fmt(Math.abs(neutralInfo.deficitPerDay))} קק"ל</b>
        </div>`);
      neutralExplain = `
        <div class="rep-explain" id="rep-neutral-explain" hidden>
          "אחזקה אמיתית" מבוססת על שינוי המשקל בפועל בתקופה הזו — כמה קק"ל היית צריכה לאכול
          בממוצע כדי לשמור בדיוק על אותו משקל. שונה מ"היעד" כי היא נגזרת מתוצאה אמיתית, לא
          מנוסחה תיאורטית. "גירעון/עודף מול הנייטרלי" משווה את מה שאכלת בפועל לאחזקה הזו —
          המספר הזה, לא זה שמול היעד, הוא שקובע אם באמת ירדת/עלית במשקל.
        </div>`;
    }
    // מסקנה — בלמי בטיחות: לא משבחים גירעון קיצוני או תמונה חלקית. מעל הכול: אם יש שינוי
    // משקל אמיתי בתקופה, הוא הקובע (אמת מדודה) — לא ה"עודף/גירעון מול היעד" (שמניח שהיעד
    // עצמו מדויק). כשהשניים סותרים (למשל: ירדת במשקל אבל "בעודף" מול יעד שגוי) — מסבירים
    // את הסתירה במפורש במקום להציג הודעה שנשמעת הפוכה מהמציאות.
    const CALORIE_FLOOR = 1200;
    const avgTooLow = report.avgCalories != null && report.avgCalories < CALORIE_FLOOR;
    const pace = report.paceKgPerWeek;
    const hasRealTrend = report.actualWeightChangeKg != null;
    const isLosingReal = hasRealTrend && pace != null && pace > 0.05;
    const isGainingReal = hasRealTrend && pace != null && pace < -0.05;
    const targetDeficit = balance != null && balance > 50;
    const targetSurplus = balance != null && balance < -50;

    if (!enoughLogging) {
      verdict = `תיעדת ${report.loggedDays} מתוך ${report.days} ימים — עוד אין תמונה מלאה. נמשיך לתעד וזה יתחדד כאן.`;
    } else if (avgTooLow) {
      verdictClass = 'warn';
      verdict = `הממוצע נמוך מאוד (${fmt(report.avgCalories!)} קק"ל) — ודאי שתיעדת את כל הארוחות. אכילה מעטה מדי אינה בריאה ועלולה להאט את הירידה.`;
    } else if (isLosingReal && targetSurplus) {
      // בדיוק המצב שבר נתקל בו: יעד לא-מדויק (נמוך מדי) גורם ל"עודף" מדומה, בזמן שבפועל יורדים
      verdictClass = 'good';
      const maint = impliedMaintenanceOf(report);
      verdict = `ירדת בפועל ~${pace!.toFixed(1)} ק"ג/שבוע — את בכיוון הנכון, למרות שאכלת בממוצע מעל היעד הנוכחי (${fmt(report.target!)} קק"ל).` +
        (maint != null ? ` זה סימן שהיעד נמוך מדי ביחס לאחזקה האמיתית שלך (~${fmt(maint.maintenance)} קק"ל) — שווה לעדכן אותו (ראי למטה) 💚` : ' כך להמשיך 💚');
    } else if (isGainingReal && targetDeficit) {
      // ההפך: לפי היעד "בגירעון", אבל במשקל בפועל עלייה — היעד כנראה גבוה מדי, או תנודתיות זמנית
      verdictClass = 'warn';
      verdict = 'לפי היעד הנוכחי נראה שאת בגירעון, אבל במשקל בפועל הייתה עלייה קלה בתקופה הזו — ' +
        'ייתכן שהיעד גבוה מדי ביחס לאחזקה האמיתית, או שזו תנודתיות זמנית (מים/מלח). נמשיך לעקוב עוד כמה ימים.';
    } else if (targetDeficit) {
      verdictClass = 'good';
      const paceTxt = pace != null && pace > 0.05 ? ` יורדת בקצב ~${pace.toFixed(1)} ק"ג/שבוע.` : '';
      verdict = `את בגירעון בריא של ~${fmt(balance!)} קק"ל ביום —${paceTxt} בדיוק בכיוון, כך להמשיך 💚`;
    } else if (targetSurplus) {
      verdictClass = 'warn';
      verdict = `את בעודף של ~${fmt(Math.abs(balance!))} קק"ל ביום לפי היעד הנוכחי. כדי לרדת — כדאי לצמצם מעט את הצריכה או להוסיף תנועה.`;
    } else if (balance != null) {
      verdict = 'את בערך על היעד (איזון) — המשקל צפוי להישמר. לירידה, כוונו לגירעון קטן.';
    }
  }

  const dateFmt2 = new Intl.DateTimeFormat('he-IL', { day: 'numeric', month: 'numeric' });
  const dateRange = report.fromDate === report.toDate
    ? dateFmt2.format(parseLocal(report.toDate))
    : `${dateFmt2.format(parseLocal(report.fromDate))} – ${dateFmt2.format(parseLocal(report.toDate))}`;

  return `
    <div class="card rep-card">
      <div class="h-row rep-head">
        <b>דוח ${periodDays === 7 ? 'שבועי' : 'חודשי'}</b>
        <div class="rep-toggle">
          <button class="rep-per${periodDays === 7 ? ' on' : ''}" data-period="7">שבוע</button>
          <button class="rep-per${periodDays === 30 ? ' on' : ''}" data-period="30">חודש</button>
        </div>
      </div>
      <div class="rep-datenav">
        <button class="rep-arrow" data-repnav="next" aria-label="התקופה הבאה"${report.isCurrent ? ' disabled' : ''}>‹</button>
        <span class="rep-daterange">${dateRange}${!report.isCurrent ? ' <button type="button" class="rep-today" data-repnav="today">חזרה להווה</button>' : ''}</span>
        <button class="rep-arrow" data-repnav="prev" aria-label="התקופה הקודמת">›</button>
      </div>
      <div class="muted rep-sub">${report.loggedDays} מתוך ${report.days} ימים תועדו</div>
      <div class="rep-rows">${rows.join('')}</div>
      ${neutralExplain}
      ${verdict ? `<div class="rep-verdict ${verdictClass}">${verdict}</div>` : ''}
    </div>`;
}

// ---------- התאמת יעד לפי משקל בפועל ----------

/** דחיית הצעה נשמרת 7 ימים — אלא אם ההצעה עצמה השתנתה משמעותית (מידע חדש = לא מציקים
 *  אבל גם לא שותקים על שינוי אמיתי). localStorage בלבד — העדפת UI, לא נתון משתמש. */
const ADAPT_DISMISS_KEY = 'litebite_adapt_dismiss';
interface AdaptDismiss { untilTs: number; calories: number }

function getAdaptDismiss(): AdaptDismiss | null {
  try { return JSON.parse(localStorage.getItem(ADAPT_DISMISS_KEY) ?? 'null') as AdaptDismiss | null; }
  catch { return null; }
}
function setAdaptDismiss(calories: number): void {
  try { localStorage.setItem(ADAPT_DISMISS_KEY, JSON.stringify({ untilTs: Date.now() + 7 * 86400000, calories })); }
  catch { /* localStorage חסום (מצב פרטי וכו') — לא קריטי, פשוט תופיע שוב */ }
}
function isAdaptDismissed(suggestedCalories: number): boolean {
  const d = getAdaptDismiss();
  return !!d && d.untilTs > Date.now() && Math.abs(d.calories - suggestedCalories) < 20;
}

function adaptiveCardHtml(res: ReturnType<typeof computeAdaptiveTarget>, currentTarget: number): string {
  const diff = res.suggestedCalories - currentTarget;
  const diffTxt = diff === 0 ? '' : ` (${diff > 0 ? '+' : ''}${fmt(diff)} מהיום)`;
  return `
    <div class="card adapt-card">
      <div class="h-row"><b>💡 התאמת יעד לפי המשקל בפועל</b></div>
      <p class="muted adapt-txt">לפי האחזקה האמיתית בדוח שלמעלה, מוצע יעד חדש כדי להמשיך בקצב
        שבחרת: <b>${fmt(res.suggestedCalories)} קק"ל</b>${diffTxt}.
        ${res.clamped ? '<br>⚠️ המספר הזה כבר ברצפת הבטיחות המחושבת (לפי ה-BMR שלך) — לא מומלץ לרדת מתחתיה.' : ''}
      </p>
      <div class="adapt-actions">
        <button class="btn-primary" id="adapt-accept">עדכון היעד ל-${fmt(res.suggestedCalories)} ✓</button>
        <button class="btn-link" id="adapt-dismiss">לא עכשיו</button>
      </div>
    </div>`;
}

/** נגזר מאותו report שכבר מוצג למעלה (לא stats נפרד) — כך שהצעת היעד תמיד מתאימה
 *  לתקופה שהמשתמשת רואה כרגע (שבוע/חודש). רק כשיש מספיק אות ופרטי פרופיל לרצפת הבטיחות. */
function renderAdaptive(
  root: HTMLElement, report: PeriodReport, targets: DailyTargets | null,
  info: AdaptiveProfileInfo | null, latestWeightKg: number | null,
): void {
  const slot = root.querySelector<HTMLElement>('#adaptive-slot');
  if (!slot) return;
  slot.innerHTML = ''; // בכל רינדור מחדש (כולל מעבר שבוע/חודש/דפדוף) — נבנה מחדש מאפס
  // הצעת עדכון-יעד שייכת רק לתקופה הנוכחית — לא הגיוני להציע יעד חדש לפי היסטוריה ישנה
  if (!report.isCurrent) return;
  if (!targets || !info || latestWeightKg == null || !reportEnoughLogging(report)) return;
  const neutralInfo = impliedMaintenanceOf(report);
  if (neutralInfo == null) return;
  const age = calcAge(info.birthdate);
  if (!Number.isFinite(age)) return;
  const res = computeAdaptiveTarget({
    impliedMaintenance: neutralInfo.maintenance,
    desiredWeeklyRateKg: info.weeklyRateKg,
    bmr: { sex: info.sex, weightKg: latestWeightKg, heightCm: info.heightCm, age },
    proteinG: info.proteinTargetG || targets.proteinG,
  });
  if (Math.abs(res.suggestedCalories - targets.calories) < 75) return; // הפרש קטן מדי — לא שווה להציק
  if (isAdaptDismissed(res.suggestedCalories)) return; // נדחתה לאחרונה ולא השתנתה מהותית
  slot.innerHTML = adaptiveCardHtml(res, targets.calories);
  slot.querySelector('#adapt-accept')?.addEventListener('click', async () => {
    try {
      await acceptAdaptiveTarget({
        calories: res.suggestedCalories, proteinG: res.suggestedProteinG,
        carbsG: res.suggestedCarbsG, fatG: res.suggestedFatG,
      });
      slot.innerHTML = '';
      void refresh(root);
    } catch (e) {
      // ⚠️ היה כאן catch ריק: הכרטיס לא זז והמשתמשת לא ידעה אם היעד אומץ. זה מסלול כתיבה,
      // ומסלול כתיבה שנכשל בשקט הוא בדיוק מה שהבראנץ' הזה בא לעקור.
      const transient = isTransient(e);
      reportError(e, { action: 'accept_adaptive_target', transient });
      toast(transient ? 'אין חיבור יציב — נסי שוב עוד רגע' : 'לא הצלחנו לעדכן את היעד', 'error');
    }
  });
  slot.querySelector('#adapt-dismiss')?.addEventListener('click', () => {
    setAdaptDismiss(res.suggestedCalories);
    slot.innerHTML = '';
  });
}

// ---------- טעינה וחיבור ----------

let reportPeriod = 7;
// כמה תקופות שלמות אחורה מהנוכחית (0 = מסתיימת היום). מתאפס בכל מעבר שבוע↔חודש —
// "2 תקופות אחורה" משמעו דבר שונה לגמרי בין 7 ל-30 יום, לא הגיוני לשמור בין המצבים.
let periodOffset = 0;
// נשמרים בין קריאות ל-loadReport (כולל לחיצה על טוגל שבוע/חודש/דפדוף) — כך שלא צריך
// לשלוף את פרטי הפרופיל מחדש בכל פעם, רק ב-refresh() המלא.
let cachedProfileInfo: AdaptiveProfileInfo | null = null;
let cachedLatestWeightKg: number | null = null;
// טווח הגרף שהמשתמשת בחרה לצפייה (ימים). נשמר בין רענונים כמו reportPeriod.
let weightRangeDays = 30;

/** קצב/ETA תמיד מתבססים על ~6 השבועות האחרונים בפועל — לא תלויים בטווח התצוגה שנבחר בגרף,
 *  כדי שבחירת "6 חודשים" לצפייה לא תשנה בהפתעה את תאריך ההגעה המוצג. */
function recentPaceFrom(trend: WeightPoint[]): number | null {
  const cutoff = parseLocal(localDateStr(new Date(Date.now() - 41 * 86400000)));
  const recent = trend.filter((p) => parseLocal(p.date) >= cutoff);
  return estimateWeeklyPace(recent.length >= 4 ? recent : trend);
}

function localDateStr(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

async function loadWeightCard(root: HTMLElement): Promise<void> {
  const slot = root.querySelector<HTMLElement>('#weight-slot');
  if (!slot) return;
  try {
    const weights = await fetchWeights(weightRangeDays);
    if (!root.isConnected) return;
    const trend = movingAverage(weights);
    const pace = recentPaceFrom(trend);
    cachedLatestWeightKg = weights.length ? weights[weights.length - 1].weightKg : null;
    slot.innerHTML = weightCardHtml(weights, trend, cachedProfileInfo, pace, weightRangeDays);
    slot.querySelectorAll<HTMLButtonElement>('[data-wtrange]').forEach((b) =>
      b.addEventListener('click', () => {
        weightRangeDays = Number(b.dataset.wtrange);
        void loadWeightCard(root);
      }),
    );
    wireWeightChartTaps(slot);
  } catch { /* הגרף לא קריטי — שאר המסך עובד */ }
}

async function loadReport(root: HTMLElement): Promise<void> {
  const slot = root.querySelector<HTMLElement>('#report-slot');
  if (!slot) return;
  try {
    const targets = await fetchTargets();
    const report = await fetchPeriodReport(reportPeriod, targets?.calories ?? null, periodOffset);
    if (!root.isConnected) return;
    slot.innerHTML = reportCardHtml(report, reportPeriod);
    slot.querySelectorAll<HTMLButtonElement>('[data-period]').forEach((b) =>
      b.addEventListener('click', () => {
        reportPeriod = Number(b.dataset.period);
        periodOffset = 0;
        void loadReport(root);
      }),
    );
    slot.querySelector('[data-repnav="prev"]')?.addEventListener('click', () => {
      periodOffset += 1;
      void loadReport(root);
    });
    slot.querySelector('[data-repnav="next"]')?.addEventListener('click', () => {
      periodOffset = Math.max(0, periodOffset - 1);
      void loadReport(root);
    });
    slot.querySelector('[data-repnav="today"]')?.addEventListener('click', () => {
      periodOffset = 0;
      void loadReport(root);
    });
    slot.querySelector('[data-neutral-info]')?.addEventListener('click', () => {
      const el = slot.querySelector<HTMLElement>('#rep-neutral-explain');
      if (el) el.hidden = !el.hidden;
    });
    renderAdaptive(root, report, targets, cachedProfileInfo, cachedLatestWeightKg);
  } catch { /* הדוח לא קריטי — שאר המסך עובד */ }
}

async function refresh(root: HTMLElement): Promise<void> {
  const body = root.querySelector<HTMLElement>('#progress-body');
  if (!body) return;
  try {
    const [weights, week, targets, profileInfo] = await Promise.all([
      fetchWeights(weightRangeDays),
      fetchWeekCalories(),
      fetchTargets(),
      fetchAdaptiveProfileInfo().catch(() => null), // לא קריטי — שאר המסך עובד בלעדיו
    ]);
    if (!root.isConnected) return;
    const trend = movingAverage(weights);
    const stats = computeWeeklyStats(week, weights); // ‏42 הימים שכבר נשלפו מכסים את חלון המגמה
    const pace = recentPaceFrom(trend); // קצב "רחב" מ-6 השבועות האחרונים בפועל — ליציבות ETA
    cachedProfileInfo = profileInfo;
    cachedLatestWeightKg = weights.length ? weights[weights.length - 1].weightKg : null;
    body.innerHTML = `
      <div id="weight-slot">${weightCardHtml(weights, trend, profileInfo, pace, weightRangeDays)}</div>
      ${tilesHtml(stats)}
      <div id="report-slot"></div>
      <div id="adaptive-slot"></div>
      ${weekCardHtml(week, targets?.calories ?? null)}`;
    const weightSlot = body.querySelector<HTMLElement>('#weight-slot');
    if (weightSlot) {
      weightSlot.querySelectorAll<HTMLButtonElement>('[data-wtrange]').forEach((b) =>
        b.addEventListener('click', () => {
          weightRangeDays = Number(b.dataset.wtrange);
          void loadWeightCard(root);
        }),
      );
      wireWeightChartTaps(weightSlot);
    }
    void loadReport(root);
  } catch {
    if (!root.isConnected) return;
    body.innerHTML = `
      <div class="card prog-empty">
        <p class="muted">לא הצלחנו לטעון את הנתונים — בדקי חיבור.</p>
        <button class="btn-primary" id="prog-retry">ניסיון חוזר</button>
      </div>`;
    body.querySelector('#prog-retry')?.addEventListener('click', () => void refresh(root));
  }
}

export function renderProgress(): string {
  return `
    <div class="progress">
      <div class="prog-head"><h2>ההתקדמות שלך 📈</h2></div>
      <div id="progress-body">
        <div class="card prog-loading">
          <div class="prog-skel"></div>
          <div class="prog-skel short"></div>
        </div>
      </div>
    </div>`;
}

export function attachProgress(root: HTMLElement): void {
  void refresh(root);
}
